import {
  Badge,
  Box,
  Group,
  MultiSelect,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
} from "@mantine/core"
import { DateInput, TimeInput } from "@mantine/dates"
import { TruckIcon, WarningIcon } from "@phosphor-icons/react"
import { UseFormReturnType } from "@mantine/form"
import { useTranslation } from "react-i18next"
import { HelpIcon } from "@dehub/theme"
import { StepMark, WizardSection } from "./WizardSection"
import type { RouteInfoForm } from "./types"

interface Props {
  form          : UseFormReturnType<RouteInfoForm>
  routeNumber   : string  // RT-YYYYMMDD-XXX preview
  zoneOptions   : Array<{ value: string; label: string }>
  warehouseOptions: Array<{ value: string; label: string; disabled?: boolean }>
  /** Where the start warehouse came from / why it cannot be changed. */
  warehouseHint : string | null
  onWarehouseChange: (value: string | null) => void
  detectedChannel: "LOCAL_DELIVERY" | "MIXED" | null
  isComplete    : boolean
}

export function RouteInfoSection({
  form,
  routeNumber,
  zoneOptions,
  warehouseOptions,
  warehouseHint,
  onWarehouseChange,
  detectedChannel,
  isComplete,
}: Props) {
  const { t } = useTranslation(["delivery"])
  return (
    <WizardSection n={1} title={t("delivery:route.wizard.step1")} helpKey="order.delivery-routes-new.section.info" helpId="sec-info" aside={isComplete ? <StepMark done>{t("delivery:route.wizard.filled")}</StepMark> : null}>
        <Stack gap="md">
          <Group grow align="flex-start">
            <TextInput
              label={t("delivery:route.wizard.name")}
              placeholder={t("delivery:route.wizard.namePlaceholder")}
              withAsterisk
              wrapperProps={{ "data-help-id": "f-name" }}
              {...form.getInputProps("name")}
            />
            <Box>
              <Text size="sm" c="gray.8" fw={500} mb={4}>
                {t("delivery:route.wizard.number")}
                <HelpIcon helpKey="order.delivery-routes-new.field.number" />
              </Text>
              <Paper p="xs" radius="sm" bg="gray.0">
                <Text fw={700} size="sm" ff="monospace">
                  {routeNumber}
                </Text>
              </Paper>
            </Box>
          </Group>

          <Group grow align="flex-start">
            <MultiSelect
              label={<>{t("delivery:route.wizard.zone")}<HelpIcon helpKey="order.delivery-routes-new.field.zone" /></>}
              placeholder={form.values.zone.length > 0 ? t("delivery:route.wizard.zoneAdd") : t("delivery:route.wizard.choose")}
              data={zoneOptions}
              clearable
              searchable
              hidePickedOptions
              nothingFoundMessage={
                zoneOptions.length === 0
                  ? t("delivery:route.wizard.zoneNone")
                  : t("delivery:route.wizard.notFound")
              }
              {...form.getInputProps("zone")}
            />
            <Box>
              <Text size="sm" c="gray.8" fw={500} mb={4}>
                {t("delivery:route.wizard.channel")}
                <HelpIcon helpKey="order.delivery-routes-new.field.channel" />
              </Text>
              <Paper p="xs" radius="sm" bg="gray.0">
                {detectedChannel === "LOCAL_DELIVERY" ? (
                  <Badge
                    color="green"
                    variant="light"
                    radius="sm"
                    leftSection={<TruckIcon size={16} weight="fill" />}
                  >
                    {t("delivery:route.wizard.channelLocal")}
                  </Badge>
                ) : detectedChannel === "MIXED" ? (
                  <Badge
                    color="orange"
                    variant="light"
                    radius="sm"
                    leftSection={<WarningIcon size={16} weight="fill" />}
                  >
                    {t("delivery:route.wizard.channelMixed")}
                  </Badge>
                ) : (
                  <Text size="sm" c="gray.7">{t("delivery:route.wizard.channelAuto")}</Text>
                )}
              </Paper>
            </Box>
          </Group>

          <Group grow align="flex-start">
            <Select
              label={<>{t("delivery:route.wizard.warehouse")}<HelpIcon helpKey="order.delivery-routes-new.field.warehouse" /></>}
              withAsterisk
              wrapperProps={{ "data-help-id": "f-warehouse" }}
              placeholder={t("delivery:route.wizard.choose")}
              description={warehouseHint}
              inputWrapperOrder={["label", "input", "description", "error"]}
              data={warehouseOptions}
              searchable
              {...form.getInputProps("warehouseId")}
              onChange={onWarehouseChange}
            />
            <Group grow>
              <DateInput
                label={<>{t("delivery:route.wizard.date")}<HelpIcon helpKey="order.delivery-routes-new.field.date" /></>}
                wrapperProps={{ "data-help-id": "f-date" }}
                placeholder="YYYY-MM-DD"
                valueFormat="YYYY-MM-DD"
                withAsterisk
                {...form.getInputProps("plannedDate")}
              />
              <TimeInput
                label={<>{t("delivery:route.wizard.startTime")}<HelpIcon helpKey="order.delivery-routes-new.field.start-time" /></>}
                {...form.getInputProps("startTime")}
              />
            </Group>
          </Group>

          <Textarea
            label={t("delivery:route.wizard.notes")}
            placeholder={t("delivery:route.wizard.notesPlaceholder")}
            autosize
            minRows={2}
            maxRows={4}
            {...form.getInputProps("notes")}
          />
        </Stack>
    </WizardSection>
  )
}
